import AddressHelper from 'src/app/helpers/address-helper';
import { BooleanHelper } from 'src/app/helpers/boolean-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { Pair } from 'src/app/helpers/interfaces/pair';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectMapHelper, {
  BoundsCoordinates,
  MARKER_COLORS,
} from 'src/app/helpers/project-map-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Coordinates from 'src/app/model/coordinates';
import Employee from 'src/app/model/product/employee';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import IPBoardPlacementType, {
  IPBoardPlacementTypeChoices,
} from 'src/app/model/product/ip-board/ip-board-placement-type';
import Address from 'src/app/model/project/address';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import { Tour } from 'src/app/model/tour/tour';
import { GeocoderService } from 'src/app/services/geocoder.service';
import {
  EmployeeTourHelper,
  PreviewInfo,
} from '../../employee-tour-list/employee-tour/helper/employee-tour-helper';
import {
  PreviewMultipleToursItem,
  PreviewSlot,
} from '../../service/tour-preview-state.service';
import {
  ImmoMapFilterObject,
  ImmoMapFilterType,
} from '../services/immo-map-controller-request.service';
import {
  ImmoMapItem,
  ImmoMapSubProject,
} from '../services/model/immo-map-sub-project';

export class ImmoMapHelper {
  private constructor() {}

  static getFilterObject(
    companyName: string,
    placementTypes: IPBoardPlacementType[],
    allSelected: boolean,
    withDateSelected: boolean,
    baseProjectSelected: boolean,
    adjustmentSelected: boolean,
    soldRentedSelected: boolean,
    repoSelected: boolean,
    taskSelected: boolean,
    notExecutedSelected: boolean,
    notInTourSelected: boolean,
    alreadyInTourSelected: boolean
  ): ImmoMapFilterObject {
    return {
      companyName: companyName,
      placementTypes: placementTypes,
      filterTypes: this.processSubProjectFilters(
        allSelected,
        withDateSelected,
        baseProjectSelected,
        adjustmentSelected,
        soldRentedSelected,
        repoSelected,
        taskSelected,
        notExecutedSelected,
        notInTourSelected,
        alreadyInTourSelected
      ),
    };
  }

  private static processSubProjectFilters(
    allSelected: boolean,
    withDateSelected: boolean,
    baseProjectSelected: boolean,
    adjustmentSelected: boolean,
    soldRentedSelected: boolean,
    repoSelected: boolean,
    taskSelected: boolean,
    notExecutedSelected: boolean,
    notInTourSelected: boolean,
    alreadyInTourSelected: boolean
  ): ImmoMapFilterType[] {
    if (allSelected) {
      return [ImmoMapFilterType.ALL];
    }
    const toReturn: ImmoMapFilterType[] = [
      withDateSelected ? ImmoMapFilterType.WITH_DATE : null,
      baseProjectSelected ? ImmoMapFilterType.BASE_PROJECT : null,
      adjustmentSelected ? ImmoMapFilterType.ADJUSTMENT : null,
      soldRentedSelected ? ImmoMapFilterType.SOLD_RENTED : null,
      repoSelected ? ImmoMapFilterType.REPO : null,
      taskSelected ? ImmoMapFilterType.TASK : null,
      notExecutedSelected ? ImmoMapFilterType.NOT_EXECUTED : null,
      notInTourSelected ? ImmoMapFilterType.NO_TOUR : null,
      alreadyInTourSelected ? ImmoMapFilterType.ALREADY_TOUR : null,
    ];
    CollectionHelper.filterInPlace(toReturn, (e) => e != null);
    return toReturn;
  }

  static constructSameCoordsArray(
    subProjects: ImmoMapSubProject[]
  ): CalculateItemGroupsResult {
    if (!CollectionHelper.isPopulated(subProjects)) {
      return {
        itemGroups: [],
        coordsToGroup: null,
      };
    }
    const groups: ItemGroup[] = [];
    let intermediateBounds: BoundsCoordinates = null;
    const coordsMap = new Map<number, ItemGroup>();
    subProjects.forEach((sp) => {
      if (sp != null && sp.address != null && sp.address.coordinates != null) {
        this.processSubProjectAppointmentInfo(sp);
        intermediateBounds = ProjectMapHelper.fitBoundsToCoordinates(
          intermediateBounds,
          sp.address.coordinates.lat,
          sp.address.coordinates.lng
        );
        const key =
          sp.address.coordinates.lat * 1000 + sp.address.coordinates.lng;
        let group = coordsMap.get(key);
        if (group == null) {
          group = {
            lat: sp.address.coordinates.lat,
            lng: sp.address.coordinates.lng,
            items: [],
            multipleAppointmentInfoDisplay: false,
            hide: false,
            clicked: false,
            hovering: false,
          };
          coordsMap.set(key, group);
        }
        if (
          group.appointmentInfoDisplay == null &&
          !StringHelper.isEmpty(sp.appointmentInfoDisplay)
        ) {
          group.appointmentInfoDisplay = sp.appointmentInfoDisplay;
        }
        group.items.push(sp);
      }
    });
    coordsMap.forEach((group) => {
      group.multipleAppointmentInfoDisplay =
        CollectionHelper.countUpTo(
          group.items,
          (sp) => !StringHelper.isEmpty(sp.appointmentInfoDisplay),
          2
        ) >= 2;
      group.markerColor = this.calculateGroupMarker(group);
      groups.push(group);
    });
    return {
      itemGroups: groups,
      coordsToGroup: coordsMap,
      latLngBounds: ProjectMapHelper.toLatLngBounds(intermediateBounds),
    };
  }

  static processSubProjectAppointmentInfo(subProject: ImmoMapSubProject): void {
    if (subProject.appointmentInfo == null) {
      return;
    }

    const appointmentLine = !subProject.appointmentInfo.appointment
      ? null
      : StringHelper.join(
          [
            'Afspraak',
            subProject.appointmentInfo.appointmentWithCustomer
              ? ' met makelaar'
              : subProject.appointmentInfo.appointmentWithCustomer === false
              ? ' met eigenaar/bewoner'
              : null,
            !StringHelper.isEmpty(subProject.appointmentInfo.appointmentTime)
              ? ' op tijdstip: ' +
                subProject.appointmentInfo.appointmentTime.slice(0, 5)
              : !StringHelper.isEmpty(
                  subProject.appointmentInfo.appointmentTimeOfDay
                )
              ? ' in daggedeelte: ' +
                subProject.appointmentInfo.appointmentTimeOfDay
              : null,
          ],
          ''
        );

    subProject.appointmentInfoDisplay = StringHelper.join(
      [
        appointmentLine,
        subProject.appointmentInfo.collectKey ? 'Sleutel afhalen' : '',
      ],
      '\n'
    );
  }

  // precondition: every item in group.items has had its appointmentInfo processed if present
  static reprocessGroupAppointmentInfo(group: ItemGroup): void {
    if (group == null) {
      return;
    }
    if (!CollectionHelper.isPopulated(group.items)) {
      group.appointmentInfoDisplay = null;
      group.multipleAppointmentInfoDisplay = false;
    } else {
      const itemtWithInfo = group.items.find(
        (s) => !StringHelper.isEmpty(s.appointmentInfoDisplay)
      );
      group.appointmentInfoDisplay =
        itemtWithInfo == null ? null : itemtWithInfo.appointmentInfoDisplay;
      group.multipleAppointmentInfoDisplay =
        CollectionHelper.countUpTo(
          group.items,
          (sp) => !StringHelper.isEmpty(sp.appointmentInfoDisplay),
          2
        ) >= 2;
    }
  }

  static processUnassignedItem(
    item: ImmoMapItem,
    itemGroups: ItemGroup[],
    coordsToGroup: Map<number, ItemGroup>,
    latLngBounds: google.maps.LatLngBounds
  ): ProcessUnassignedItemResult {
    let group = null;
    if (
      item != null &&
      item.address != null &&
      item.address.coordinates != null
    ) {
      const key =
        item.address.coordinates.lat * 1000 + item.address.coordinates.lng;
      group = coordsToGroup.get(key);
      const groupWasNull = group == null;
      if (group == null) {
        group = {
          lat: item.address.coordinates.lat,
          lng: item.address.coordinates.lng,
          items: [item],
          hide: false,
          clicked: false,
        };
        itemGroups.push(group);
        coordsToGroup.set(key, group);
      } else {
        group.items.push(item);
      }

      if (groupWasNull || group.hide) {
        let intermediateBounds: BoundsCoordinates =
          latLngBounds == null
            ? ProjectMapHelper.fitBoundsToCoordinates(
                null,
                item.address.coordinates.lat,
                item.address.coordinates.lng
              )
            : ProjectMapHelper.toBoundsCoordinates(latLngBounds);
        intermediateBounds =
          latLngBounds == null
            ? intermediateBounds
            : ProjectMapHelper.fitBoundsToCoordinates(
                intermediateBounds,
                item.address.coordinates.lat,
                item.address.coordinates.lng
              );
        latLngBounds = ProjectMapHelper.toLatLngBounds(intermediateBounds);
      }

      group.markerColor = this.calculateGroupMarker(group);
      this.reprocessGroupAppointmentInfo(group);
    }

    group.hide = false;

    return {
      group: group,
      latLngBounds: latLngBounds,
    };
  }

  static calculateGroupMarker(itemGroup: ItemGroup): string {
    if (!CollectionHelper.isPopulated(itemGroup.items)) {
      return MARKER_COLORS.GREEN_MARKER;
    }
    let intermediate: string = null;
    for (const item of itemGroup.items) {
      intermediate = this.calculateItemMarker(
        item,
        item.type != 'Task' ? this.calculateSelectStonesMarker(item) : false
      );
      if (
        intermediate === MARKER_COLORS.GREEN_MARKER ||
        intermediate === MARKER_COLORS.GREEN_MARKER_STONES ||
        intermediate === MARKER_COLORS.YELLOW_MARKER ||
        intermediate === MARKER_COLORS.PURPLE_MARKER
      ) {
        return intermediate;
      }
    }
    return intermediate;
  }

  private static calculateSelectStonesMarker(
    subProject: ImmoMapSubProject
  ): boolean {
    return (
      subProject.project != null &&
      CollectionHelper.isPopulated(subProject.project.products) &&
      subProject.project.products.some((p) => {
        if (!ProductHelper.isIPBoard(p)) {
          return false;
        }

        const ipBoard = <IPBoard>p;
        return (
          ipBoard.boardPart != null &&
          ipBoard.boardPart.placementType != null &&
          ipBoard.boardPart.placementType.ordinal ===
            IPBoardPlacementTypeChoices.ON_STONES.ordinal
        );
      })
    );
  }

  private static calculateItemMarker(
    item: ImmoMapItem,
    selectStonesMarker: boolean
  ): string {
    switch (item.type) {
      case SUB_PROJECT_TYPES.BASE_PROJECT:
        return selectStonesMarker
          ? MARKER_COLORS.GREEN_MARKER_STONES
          : MARKER_COLORS.GREEN_MARKER;
      case SUB_PROJECT_TYPES.ADJUSTMENT:
        return selectStonesMarker
          ? MARKER_COLORS.BLUE_MARKER_STONES
          : MARKER_COLORS.BLUE_MARKER;
      case SUB_PROJECT_TYPES.SOLDRENTED:
        return selectStonesMarker
          ? MARKER_COLORS.YELLOW_MARKER_STONES
          : MARKER_COLORS.YELLOW_MARKER;
      case SUB_PROJECT_TYPES.REPO:
        return selectStonesMarker
          ? MARKER_COLORS.RED_MARKER_STONES
          : MARKER_COLORS.RED_MARKER;
      case 'Task':
        return MARKER_COLORS.PURPLE_MARKER;
      default:
        return selectStonesMarker
          ? MARKER_COLORS.GREEN_MARKER_STONES
          : MARKER_COLORS.GREEN_MARKER;
    }
  }

  static getBoundsForItemGroups(
    itemGroups: ItemGroup[]
  ): google.maps.LatLngBounds {
    if (!CollectionHelper.isPopulated(itemGroups)) {
      return null;
    }

    let intermediateBounds: BoundsCoordinates = null;
    itemGroups.forEach((spg) => {
      if (!spg.hide) {
        intermediateBounds = ProjectMapHelper.fitBoundsToCoordinates(
          intermediateBounds,
          spg.lat,
          spg.lng
        );
      }
    });

    return ProjectMapHelper.toLatLngBounds(intermediateBounds);
  }

  static getBoundsForTourPreviewMarkers(
    previewSlots: PreviewSlot[]
  ): google.maps.LatLngBounds {
    if (!CollectionHelper.isPopulated(previewSlots)) {
      return null;
    }

    let intermediateBounds: BoundsCoordinates = null;

    previewSlots.forEach((s) => {
      if (s.routeBounds != null) {
        intermediateBounds = {
          northBound: s.routeBounds.getNorthEast().lat(),
          southBound: s.routeBounds.getSouthWest().lat(),
          eastBound: s.routeBounds.getNorthEast().lng(),
          westBound: s.routeBounds.getSouthWest().lng(),
        };
      } else if (CollectionHelper.isPopulated(s.markers)) {
        s.markers.forEach(
          (m) =>
            (intermediateBounds = ProjectMapHelper.fitBoundsToCoordinates(
              intermediateBounds,
              m.lat,
              m.lng
            ))
        );
      }
    });

    return ProjectMapHelper.toLatLngBounds(intermediateBounds);
  }

  static shouldCenterOnTourPreviews(
    previousPreviewSlots: PreviewSlot[],
    newPreviewSlots: PreviewSlot[]
  ): boolean {
    if (!CollectionHelper.isPopulated(newPreviewSlots)) {
      return false;
    }
    const previousNewPairs = this.constructPreviousNewPairs(
      previousPreviewSlots,
      newPreviewSlots
    );
    let atLeastOneHasMarkers = false;
    let xorCollapse = false;
    for (const pair of previousNewPairs) {
      atLeastOneHasMarkers = atLeastOneHasMarkers || pair.right;
      xorCollapse = xorCollapse || BooleanHelper.xor(pair.left, pair.right);
    }
    return atLeastOneHasMarkers && xorCollapse;
  }

  private static constructPreviousNewPairs(
    previousPreviewSlots: PreviewSlot[],
    newPreviewSlots: PreviewSlot[]
  ): Pair<boolean, boolean>[] {
    const previousPreviewSlotsPopulated =
      CollectionHelper.isPopulated(previousPreviewSlots);
    const newPreviewSlotsPopulated =
      CollectionHelper.isPopulated(newPreviewSlots);
    if (!previousPreviewSlotsPopulated && !newPreviewSlotsPopulated) {
      return null;
    }
    if (!previousPreviewSlotsPopulated) {
      return newPreviewSlots.map((s) => {
        return {
          left: false,
          right: CollectionHelper.isPopulated(s.markers),
        };
      });
    }
    if (!newPreviewSlotsPopulated) {
      return previousPreviewSlots.map((s) => {
        return {
          left: CollectionHelper.isPopulated(s.markers),
          right: false,
        };
      });
    }

    const toReturn: Pair<boolean, boolean>[] = [];
    let newCursor = 0;

    for (
      let previousCursor = 0;
      previousCursor < previousPreviewSlots.length;
      previousCursor++
    ) {
      const previousPreviewSlot = previousPreviewSlots[previousCursor];
      let matchFound = false;

      while (newCursor < newPreviewSlots.length) {
        const newPreviewSlot = newPreviewSlots[newCursor];
        if (previousPreviewSlot.id < newPreviewSlot.id) {
          break;
        }
        if (previousPreviewSlot.id === newPreviewSlot.id) {
          toReturn.push({
            left: CollectionHelper.isPopulated(previousPreviewSlot.markers),
            right: CollectionHelper.isPopulated(newPreviewSlot.markers),
          });
          matchFound = true;
          newCursor++;
          break;
        } else {
          toReturn.push({
            left: false,
            right: CollectionHelper.isPopulated(
              newPreviewSlots[newCursor].markers
            ),
          });
          newCursor++;
        }
      }

      if (!matchFound) {
        toReturn.push({
          left: CollectionHelper.isPopulated(previousPreviewSlot.markers),
          right: false,
        });
      }
    }

    while (newCursor < newPreviewSlots.length) {
      toReturn.push({
        left: false,
        right: CollectionHelper.isPopulated(newPreviewSlots[newCursor].markers),
      });
      newCursor++;
    }

    return toReturn;
  }

  static getPreviewMultipleToursItems(
    tours: Tour[],
    geocoderService: GeocoderService
  ): Promise<PreviewMultipleToursItem[]> {
    if (!CollectionHelper.isPopulated(tours)) {
      return Promise.resolve([]);
    }

    return Promise.all(
      tours.map((t) => {
        return new Promise<PreviewMultipleToursItem>((resolve) => {
          this.getCoordinatesForAddress(t.placer.address, geocoderService).then(
            (coordinates: Coordinates) => {
              const placerAddressPreviewInfo: PreviewInfo =
                coordinates == null
                  ? null
                  : {
                      subProjectId: null,
                      immoTaskId: null,
                      onStones: false,
                      coordinates: coordinates,
                      collectKey: false,
                    };
              const previewInfo = EmployeeTourHelper.getPreviewInfoFromTour(t);
              const toSetPreviewInfo =
                placerAddressPreviewInfo == null
                  ? previewInfo
                  : [placerAddressPreviewInfo, ...previewInfo];

              resolve({
                placer: t.placer,
                date: t.date,
                placerAddressCoordinatesIncluded: coordinates != null,
                previewInfo: toSetPreviewInfo,
              });
            }
          );
        });
      })
    );
  }

  private static getCoordinatesForAddress(
    address: Address,
    geocoderService: GeocoderService
  ): Promise<Coordinates> {
    return new Promise((resolve) => {
      if (address == null) {
        resolve(null);
      } else {
        geocoderService
          .geocodeAddress(AddressHelper.getAddressDisplay(address))
          .then((coordinates: Coordinates) => resolve(coordinates))
          .catch((error: Error) => {
            console.error(error);
            resolve(null);
          });
      }
    });
  }
}

export interface ItemGroup {
  lat: number;
  lng: number;
  items: ImmoMapItem[];
  markerColor?: string;
  appointmentInfoDisplay?: string;
  multipleAppointmentInfoDisplay: boolean;
  hide: boolean;
  clicked: boolean;
  hovering: boolean;
}

export interface CalculateItemGroupsResult {
  itemGroups: ItemGroup[];
  coordsToGroup: Map<number, ItemGroup>;
  latLngBounds?: google.maps.LatLngBounds;
}

export interface ProcessUnassignedItemResult {
  group: ItemGroup;
  latLngBounds: google.maps.LatLngBounds;
}

export interface LastSelectedPlanValues {
  selectedPlacer: Employee;
}

export interface GroupSelectedEvent {
  group: ItemGroup;
  lastSelectedPlanValues?: LastSelectedPlanValues;
}

export interface TourPreviewMarker {
  subProjectId: number;
  immoTaskId: number;
  clicked: boolean;
  hovering: boolean;
  label: string;
  pathSuffix: string;
  labelOrigin: google.maps.Point;
  lat: number;
  lng: number;
}

export interface PreviewMarkerSelectedEvent {
  subProjectId: number;
  immoTaskId: number;
  lastSelectedPlanValues?: LastSelectedPlanValues;
}

export interface ItemPlannedEvent {
  subProjectId: number;
  immoTaskId: number;
  lastSelectedPlanValues: LastSelectedPlanValues;
}
